import { Suspense, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import axios from "axios";

import { useNavigate, useSearchParams, useLocation, NavLink } from "react-router-dom";
import { Container, Row, Col, Modal, InputGroup, Form, Button } from "react-bootstrap";

import Loader from "../common/loader";
import SearchResults from "./search.results";
import { modalState } from "./search.state";
import ErrorBoundary from "../common/error-boundary";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [modal, setModal] = useRecoilState(modalState);
  const query = searchParams.get("query");

  const [maps, setMaps] = useState({
    tabular: new Map(),
    neoplasm: new Map(),
    drug: new Map(),
    injury: new Map(),
    icdo3: [],
    icd11: new Map(),
    icdo4: [],
    icd10pcs: []
  })
  const [input, setInput] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [valid, setValid] = useState(true)
  const [suggestions, setSuggestions] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [totals, setTotals] = useState({})
  const [resultLimit, setResultLimit] = useState(0)

  useEffect(() => {

    if (location.state) {
      setInput(location.state.query)
      if (location.state.query.length >= 3)
        handleSubmit(location.state.query)
      else
        setValid(false)
    }

    window.history.replaceState(null, '')
  },
    [location]);

  function hideModal() {
    setModal((state) => ({ ...state, show: false }));
  }

  function processSearch(results) {

    const map = new Map();
    results.map((node) => {

      const source = node._source;
      const key = source.id;
      var parents = [];
      var currentKey = key;

      source.parent.reverse().map((parent) => {
        const parentKey = parent.id;

        if (!map.has(parentKey)) {

          var value;

          value = {
            description: parent.description,
            parents: parent.parents,
            children: [currentKey],
            key: parentKey,
          }

          if (node._index === "drug" || node._index === "neoplasm") {
            for (var code in parent.code)
              value[code] = parent.code[code]
          }
          else
            value["code"] = parent.code

          map.set(parentKey, value)
        }
        else {

          var parentValue = map.get(parentKey)
          if (!parentValue.children.includes(currentKey)) {
            parentValue = { ...parentValue, children: parentValue.children.concat(currentKey) }
            map.set(parentKey, parentValue)
          }
        }

        currentKey = parentKey
        parents = parents.concat(parentKey)
      })

      const value = {
        description: source.description,
        key: key,
        parents: parents,
        children: [],
      }

      if (node._index === "drug" || node._index === "neoplasm") {
        for (var code in source.code)
          value[code] = source.code[code]
      }
      else
        value["code"] = source.code

      map.set(key, value)
    })
    return map
  }

  function processIcd11Search(results) {
    const map = new Map();
    results.map((node) => {
      const source = node._source;
      const key = source.id;
      var currentKey = key;

      source.parent.forEach((parent, index) => {
        const parentKey = parent.id;
        // This parent's own parents are the remaining items after it in the array
        const parentOfParent = source.parent.slice(index + 1).map(p => p.id);

        if (!map.has(parentKey)) {
          map.set(parentKey, {
            description: parent.description,
            code: parent.code,
            parents: parentOfParent,
            children: [currentKey],
            key: parentKey,
          })
        } else {
          var parentValue = map.get(parentKey)
          if (!parentValue.children.includes(currentKey)) {
            parentValue = { ...parentValue, children: parentValue.children.concat(currentKey) }
            map.set(parentKey, parentValue)
          }
        }

        currentKey = parentKey
      })

      // The leaf node's parents are all items in the parent array
      map.set(key, {
        description: source.description,
        code: source.code,
        key: key,
        parents: source.parent.map(p => p.id),
        children: [],
      })
    })
    return map
  }

  async function handleSubmit(query) {

    setLoading(true)
    setValid(true)
    setInput(query)
    setSearchTerm(query)
    try {
      const response = await axios.post("api/search", { search: query })
      const results = {
        tabular: processSearch(response.data.tabular.filter((e) => e._source.type === "entry")),
        neoplasm: processSearch(response.data.neoplasm),
        drug: processSearch(response.data.drug),
        injury: processSearch(response.data.injury),
        icdo3: response.data.icdo3,
        icd11: response.data.icd11 ? processIcd11Search(response.data.icd11) : new Map(),
        icdo4: response.data.icdo4 || [],
        icd10pcs: response.data.icd10pcs || []
      }
      console.log(results)
      setSuggestions(response.data.fuzzyTerms)
      setSubmitted(true)
      setMaps(results)
      setTotals(response.data.totals || {})
      setResultLimit(response.data.resultLimit || 0)
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      setLoading(false)
    }
  }

  async function opensearch(e) {
    e.preventDefault();
    if (input.length >= 3)
      handleSubmit(input);
    else
      setValid(false)
  }

  return (
    <>
      <Loader show={loading} fullscreen />
      <Modal show={modal.show} size="xl" onHide={hideModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modal.body}</Modal.Body>
      </Modal>
      <div className="h-100 bg-white">

        <Container className="flex-grow-1 py-5">
          <Row className="h-100 justify-content-center align-items-center">
            <Col md={8}>
              <form onSubmit={opensearch}>
                <InputGroup size={"lg"} className="search-box" style={{ borderColor: valid ? "" : "red" }}>
                  <Form.Control
                    className="border-0 shadow-none"
                    placeholder={"Search by Keywords, ICD-10 code, ICD-O-3 code, ICD-11 code or ICD-O-4 code"}
                    aria-label={"Search by Keywords, ICD-10 code, ICD-O-3 code, ICD-11 code or ICD-O-4 code"}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onBlur={() => setValid(input.length >= 3)}
                  />
                  <Button type="submit" variant="white" className="border-0 shadow-none">
                    <span className="visually-hidden">Submit</span>
                    <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
                  </Button>
                </InputGroup>
                {!valid && <div style={{ color: "#AD0000" }}>
                  Your search text must be at least 3 characters long
                </div>}
              </form>
              {
                suggestions.length ? <span style={{ fontSize: '18px' }}>
                  Did you mean: {(suggestions.map((e, index) =>
                    <>
                      {index ? ', ' : ''}
                      <a href="javascript:void(0)" onClick={() => handleSubmit(e)}>{e}</a>
                    </>
                  ))}
                </span> :
                  submitted && maps.tabular.size === 0 && maps.neoplasm.size === 0 && maps.drug.size === 0 && maps.injury.size === 0 && maps.icdo3.length === 0 && maps.icd11.size === 0 && maps.icdo4.length === 0 && maps.icd10pcs.length === 0?
                    <span style={{ color: "#AD0000" }} className="mx-1">No Results Found</span>
                    : <></>}

            </Col>
          </Row>
        </Container>

        <ErrorBoundary fallback="">
          <Suspense fallback={<Loader show fullscreen />}>
            <SearchResults query={query} maps={maps} search={searchTerm} totals={totals} resultLimit={resultLimit} />
          </Suspense>
        </ErrorBoundary>
      </div>
    </>

  );
}
